import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiHealthService, APILog, HealthStats, GatewayConfig, TestResult } from '../services/apiHealthService';
import LanguageSwitcher from '../components/LanguageSwitcher';

const APIHealthPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'gateways' | 'config'>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [healthStats, setHealthStats] = useState<HealthStats | null>(null);
  const [apiLogs, setApiLogs] = useState<APILog[]>([]);
  const [gatewayConfig, setGatewayConfig] = useState<GatewayConfig | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult> | null>(null);
  
  // Filters
  const [logFilter, setLogFilter] = useState<string>('all');
  const [logLimit, setLogLimit] = useState<number>(50);

  const loadOverviewData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsResponse, configResponse] = await Promise.all([
        apiHealthService.getHealthStats(),
        apiHealthService.getGatewayConfig()
      ]);

      if (statsResponse.success && statsResponse.data) {
        setHealthStats(statsResponse.data);
      }

      if (configResponse.success && configResponse.data) {
        setGatewayConfig(configResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadOverviewData();
  }, [loadOverviewData]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiHealthService.getRecentLogs(
        logLimit, 
        logFilter === 'all' ? undefined : logFilter
      );

      if (response.success && response.data) {
        setApiLogs(response.data.logs);
      } else {
        setError(response.error || 'Failed to load logs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [logLimit, logFilter]);

  const testAllGateways = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiHealthService.testAllGateways();
      
      if (response.success && response.data) {
        setTestResults(response.data.gateways);
      } else {
        setError(response.error || 'Failed to test gateways');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to test gateways');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiHealthService.getGatewayConfig();
      
      if (response.success && response.data) {
        setGatewayConfig(response.data);
      } else {
        setError(response.error || 'Failed to load configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'logs':
        loadLogs();
        break;
      case 'gateways':
        testAllGateways();
        break;
      case 'config':
        loadConfig();
        break;
      default:
        break;
    }
  }, [activeTab, logFilter, logLimit, loadLogs, testAllGateways, loadConfig]);

  const StatCard: React.FC<{ title: string; value: string | number; subtitle?: string; color?: string }> = ({ 
    title, 
    value, 
    subtitle, 
    color = 'blue' 
  }) => (
    <div className={`bg-${color}-500/20 border border-${color}-500/30 rounded-lg p-4`}>
      <h3 className={`text-${color}-200 font-semibold text-sm mb-1`}>{title}</h3>
      <p className={`text-${color}-100 text-2xl font-bold`}>{value}</p>
      {subtitle && <p className={`text-${color}-200 text-xs mt-1`}>{subtitle}</p>}
    </div>
  );

  const LogRow: React.FC<{ log: APILog }> = ({ log }) => (
    <tr className="border-b border-white/10 hover:bg-white/5">
      <td className="px-4 py-3 text-sm text-white/80">
        {apiHealthService.formatTimestamp(log.timestamp)}
      </td>
      <td className="px-4 py-3 text-sm">
        <span className="flex items-center space-x-2">
          <span>{apiHealthService.getGatewayIcon(log.gateway)}</span>
          <span className="text-white">{apiHealthService.getGatewayDisplayName(log.gateway)}</span>
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-white/80">
        <span className="font-mono">{log.method}</span>
      </td>
      <td className="px-4 py-3 text-sm text-white/80 font-mono">
        {log.endpoint}
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={`px-2 py-1 rounded-full text-xs ${apiHealthService.getStatusBadgeColor(log.status, log.success)}`}>
          {log.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-white/80">
        {log.duration}ms
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={apiHealthService.getStatusColor(log.status, log.success)}>
          {log.success ? '✅' : '❌'}
        </span>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800">
      <div className="container mx-auto px-4 py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">🔍 API Health Monitor</h1>
          <p className="text-white/90 text-lg">Monitor payment gateway performance and API responses</p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white/10 rounded-lg p-1 mb-6">
          <div className="flex space-x-1">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'logs', label: 'API Logs', icon: '📝' },
              { id: 'gateways', label: 'Gateway Tests', icon: '🔧' },
              { id: 'config', label: 'Configuration', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-md transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <p className="text-red-200">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-300 hover:text-red-100 text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <span className="ml-3 text-white">{t('common.loading')}</span>
          </div>
        )}

        {/* Tab Content */}
        {!loading && (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && healthStats && (
              <div className="space-y-6">
                {/* Overall Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard
                    title="Total API Calls"
                    value={healthStats.total.calls}
                    subtitle={`${healthStats.total.successRate}% success rate`}
                    color="blue"
                  />
                  <StatCard
                    title="Last Hour"
                    value={healthStats.lastHour.calls}
                    subtitle={`${healthStats.lastHour.successRate}% success rate`}
                    color="green"
                  />
                  <StatCard
                    title="Last 24 Hours"
                    value={healthStats.last24Hours.calls}
                    subtitle={`${healthStats.last24Hours.successRate}% success rate`}
                    color="purple"
                  />
                  <StatCard
                    title="Failed Calls"
                    value={healthStats.total.failed}
                    subtitle={`${((healthStats.total.failed / healthStats.total.calls) * 100).toFixed(1)}% failure rate`}
                    color="red"
                  />
                </div>

                {/* Gateway Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="mr-2">💳</span>
                      Razorpay
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">Total Calls:</span>
                        <span className="text-white font-semibold">{healthStats.gateways.razorpay.calls}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Success Rate:</span>
                        <span className="text-green-400 font-semibold">
                          {healthStats.gateways.razorpay.calls > 0 
                            ? ((healthStats.gateways.razorpay.success / healthStats.gateways.razorpay.calls) * 100).toFixed(1)
                            : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Avg Duration:</span>
                        <span className="text-white font-semibold">{healthStats.gateways.razorpay.avgDuration}ms</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="mr-2">📱</span>
                      QR Payment
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">Total Calls:</span>
                        <span className="text-white font-semibold">{healthStats.gateways.qr_payment.calls}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Success Rate:</span>
                        <span className="text-green-400 font-semibold">
                          {healthStats.gateways.qr_payment.calls > 0 
                            ? ((healthStats.gateways.qr_payment.success / healthStats.gateways.qr_payment.calls) * 100).toFixed(1)
                            : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Avg Duration:</span>
                        <span className="text-white font-semibold">{healthStats.gateways.qr_payment.avgDuration}ms</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div className="space-y-4">
                {/* Filters */}
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Gateway Filter:</label>
                      <select
                        value={logFilter}
                        onChange={(e) => setLogFilter(e.target.value)}
                        className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="all">All Gateways</option>
                        <option value="razorpay">Razorpay</option>
                        <option value="qr_payment">QR Payment</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Limit:</label>
                      <select
                        value={logLimit}
                        onChange={(e) => setLogLimit(Number(e.target.value))}
                        className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                      >
                        <option value={25}>25 logs</option>
                        <option value={50}>50 logs</option>
                        <option value={100}>100 logs</option>
                        <option value={200}>200 logs</option>
                      </select>
                    </div>
                    <button
                      onClick={loadLogs}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Logs Table */}
                <div className="bg-white/10 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Timestamp</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Gateway</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Method</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Endpoint</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Duration</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-white/80">Success</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiLogs.map((log) => (
                          <LogRow key={log.id} log={log} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {apiLogs.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-white/60">No logs found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gateway Tests Tab */}
            {activeTab === 'gateways' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Gateway Connectivity Tests</h2>
                  <button
                    onClick={testAllGateways}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
                  >
                    Test All Gateways
                  </button>
                </div>

                {testResults && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(testResults).map(([gateway, result]) => (
                      <div key={gateway} className="bg-white/10 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <span className="mr-2">{apiHealthService.getGatewayIcon(gateway)}</span>
                          {apiHealthService.getGatewayDisplayName(gateway)}
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-white/80">Status:</span>
                            <span className={`font-semibold ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                              {result.success ? '✅ Connected' : '❌ Failed'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/80">Response Time:</span>
                            <span className="text-white font-semibold">{result.duration}ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/80">HTTP Status:</span>
                            <span className="text-white font-semibold">{result.status}</span>
                          </div>
                          <div className="mt-4">
                            <span className="text-white/80 text-sm">Message:</span>
                            <p className="text-white text-sm mt-1">{result.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Configuration Tab */}
            {activeTab === 'config' && gatewayConfig && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white">Gateway Configuration</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="mr-2">💳</span>
                      Razorpay
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">Configured:</span>
                        <span className={`font-semibold ${gatewayConfig.razorpay.configured ? 'text-green-400' : 'text-red-400'}`}>
                          {gatewayConfig.razorpay.configured ? '✅ Yes' : '❌ No'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Key ID:</span>
                        <span className="text-white font-mono text-sm">{gatewayConfig.razorpay.keyId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Secret Key:</span>
                        <span className={`font-semibold ${gatewayConfig.razorpay.hasSecret ? 'text-green-400' : 'text-red-400'}`}>
                          {gatewayConfig.razorpay.hasSecret ? '✅ Set' : '❌ Not Set'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <span className="mr-2">📱</span>
                      QR Payment
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">Configured:</span>
                        <span className={`font-semibold ${gatewayConfig.qr_payment.configured ? 'text-green-400' : 'text-red-400'}`}>
                          {gatewayConfig.qr_payment.configured ? '✅ Yes' : '❌ No'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Secret ID:</span>
                        <span className="text-white font-mono text-sm">{gatewayConfig.qr_payment.secretId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Secret Key:</span>
                        <span className={`font-semibold ${gatewayConfig.qr_payment.hasSecret ? 'text-green-400' : 'text-red-400'}`}>
                          {gatewayConfig.qr_payment.hasSecret ? '✅ Set' : '❌ Not Set'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Base URL:</span>
                        <span className="text-white font-mono text-sm">{gatewayConfig.qr_payment.baseUrl}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default APIHealthPage;
